import React, { useEffect, useState } from 'react';
import { DialogProps, FormHelperText, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';

import { useDefineUserRole, useFormatMessage } from '../../../../../hooks';
import { setLayoutSubheader, setLayoutFooter } from '../../../../../utils/layout';
import { useGetExample, useImportOffer } from './useOffer';
import AlertDialog from '../../../../../components/ui/Dialog/AlertDialog';
import { useGetCompanies } from './useGetCompanies';
import { IAppState } from '../../../../../store/rootDuck';
import CompaniesTable from './CompaniesTable';
import { Modal } from '../../../../../components/other/Modals';
import { ButtonWithLoader } from '../../../../../components/other/Buttons';
import { getPublicDoc } from '../../../../../crud/docs.crud';

export function ImportOffers() {
  const fm = useFormatMessage();
  const { classes } = useStyles();
  const { me } = useSelector((store: IAppState) => ({
    me: store.profile.me,
  }));
  const isManager = useDefineUserRole(me, ['ROLE_MANAGER']);
  const isVendor = useDefineUserRole(me, ['ROLE_VENDOR', 'ROLE_VENDOR_STAFF']);
  const { getExample } = useGetExample();
  const [isAlertOpen, setAlertOpen] = React.useState(false);
  const [openDialogCompanies, setOpenDialogCompanies] = React.useState(false);
  const { upload, loadingUpload, successUpload, errors } = useImportOffer();
  const [uploadFile, setFile] = React.useState<FormData | null>(null);
  const {
    fetch: fetchCompanies,
    loading: loadingCompanies,
    page,
    companies,
  } = useGetCompanies();
  const [instructionText, setInstructionText] = useState<string>('');
  const [loadingInstruction, setLoadingInstruction] = useState<boolean>(true);

  const handleDrop = async (droppedFiles: File[]) => {
    const file = new FormData();
    file.append('file', droppedFiles[0]);
    if (me?.is_admin || isManager) {
      fetchCompanies(1, 20, 'ROLE_VENDOR');
      setFile(file);
      setOpenDialogCompanies(true);
    } else {
      upload({ file });
    }
  };

  const { open } = useDropzone({ onDrop: handleDrop });
  setLayoutSubheader({
    title: fm('IMPORT.OFFERS'),
  });
  setLayoutFooter({ show: true });

  useEffect(() => {
    successUpload && setAlertOpen(true);
  }, [successUpload]);

  useEffect(() => {
    setLoadingInstruction(true);
    getPublicDoc('import_instruction')
      .then(({ data: requestData }) => {
        setInstructionText(requestData.data?.text || '');
        setLoadingInstruction(false);
      })
      .catch(() => {
        setInstructionText('');
        setLoadingInstruction(false);
      });
  }, []);

  if (!me?.is_admin && !isManager && !isVendor) return null;

  return (
    <div className={classes.container}>
      <div className={classes.containerBtns}>
        <ButtonWithLoader onPress={getExample}>
          {fm('DOWNLOAD.EXAMPLE.OFFER')}
        </ButtonWithLoader>
        <ButtonWithLoader onPress={() => !loadingUpload && open()} loading={loadingUpload}>
          {fm('UPLOAD.OFFER')}
        </ButtonWithLoader>
      </div>
      {errors.map(i => (
        <FormHelperText error className={classes.helperText} key={i}>
          {i}
        </FormHelperText>
      ))}
      {loadingInstruction && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 30 }}>
          <CircularProgress />
        </div>
      )}
      {!loadingInstruction && instructionText && (
        <div
          className={classes.instructionContainer}
          dangerouslySetInnerHTML={{ __html: instructionText }}
        />
      )}

      <AlertDialog
        open={isAlertOpen}
        title={fm('UPLOAD.OFFER.SUCCESS.TITLE')}
        message={fm('UPLOAD.OFFER.SUCCESS.TEXT')}
        cancelText={fm('CATEGORIES.DELETE.OK')}
        okText=''
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleAgree={() => {
          setAlertOpen(false);
        }}
      />

      <Modal
        DialogProps={{ maxWidth: 'md' } as DialogProps}
        open={openDialogCompanies}
        onClose={loadingUpload ? () => {} : () => setOpenDialogCompanies(false)}
        title={fm('COMPANY.EDIT.MOUNT_TITLE')}
        loading={loadingUpload || loadingCompanies}
        content={
          <CompaniesTable
            page={page.page}
            perPage={page.perPage}
            total={page.total}
            onConnect={company => {
              uploadFile && upload({ file: uploadFile, company_id: company.id });
              setOpenDialogCompanies(false);
            }}
            connectedCompanies={[]}
            companies={companies || []}
            fetch={({ page, perPage }) => fetchCompanies(page, perPage, 'ROLE_VENDOR')}
          />
        }
        actions={[
          {
            title: fm('COMMON.BUTTON.CLOSE'),
            onClick: () => setOpenDialogCompanies(false),
          },
        ]}
      />
    </div>
  );
}

const useStyles = makeStyles()(() => {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    containerBtns: {
      display: 'flex',
      flexDirection: 'row',
      gap: 20,
    },
    helperText: {
      fontSize: 17,
    },
    instructionContainer: {
      marginTop: 30,
      backgroundColor: '#fff',
      padding: 20,
      borderRadius: 8,
      whiteSpace: 'pre-line',
      color: '#000000',
      fontWeight: 500,
      '& p': {
        margin: '8px 0',
        color: '#000000',
        fontWeight: 500,
      },
      '& *': {
        color: 'inherit',
        fontWeight: 'inherit',
      },
      '& strong, & b': {
        fontWeight: 700,
      },
    },
  };
});
